/**
 * Google Analytics 4 (gtag) — Pamagach. Same property as petitions.pro / zvarot.info; each site uses its own Measurement ID via env.
 */
import CROSS_DOMAIN_HOSTS from "../constants/ga4-domains.json";

export const PLATFORM_SOURCE = "pamaga";

const MEASUREMENT_ID = process.env.GATSBY_GA_MEASUREMENT_ID || "";
const GA_DEBUG = process.env.GATSBY_GA_DEBUG === "1";

function callGtag(...args) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag(...args);
}

export function isGa4Enabled() {
  return Boolean(MEASUREMENT_ID);
}

/**
 * @param {object} params
 * @param {string} [params.topic_sector] — parent category name or sentinel e.g. "improve"
 */
export function trackPamagaGenerationComplete({ topic_sector } = {}) {
  if (!MEASUREMENT_ID) return;
  const payload = {
    platform_source: PLATFORM_SOURCE,
    ...(topic_sector != null && topic_sector !== "" ? { topic_sector: String(topic_sector) } : {}),
  };
  if (GA_DEBUG) payload.debug_mode = true;
  callGtag("event", "pamaga_generation_complete", payload);
}

/**
 * User submitted a free-text query on /new.
 */
export function trackPamagaQuerySubmitted() {
  if (!MEASUREMENT_ID) return;
  const payload = { platform_source: PLATFORM_SOURCE };
  if (GA_DEBUG) payload.debug_mode = true;
  callGtag("event", "pamaga_query_submitted", payload);
}

/**
 * A card was matched and its generated document was shown to the user.
 * @param {object} params
 * @param {string} [params.topic_sector] — matched card sector
 */
export function trackPamagaCardShown({ topic_sector } = {}) {
  if (!MEASUREMENT_ID) return;
  const payload = {
    platform_source: PLATFORM_SOURCE,
    ...(topic_sector != null && topic_sector !== "" ? { topic_sector: String(topic_sector) } : {}),
  };
  if (GA_DEBUG) payload.debug_mode = true;
  callGtag("event", "pamaga_card_shown", payload);
}

/**
 * Outbound click to partner domains (cross-platform navigation).
 * @param {object} params
 * @param {string} params.link_url
 * @param {string} [params.link_domain]
 * @param {string} [params.destination_platform] — optional label e.g. petitions / zvarot
 */
export function trackCrossPlatformNavigation({
  link_url,
  link_domain,
  destination_platform,
} = {}) {
  if (!MEASUREMENT_ID || !link_url) return;
  const payload = {
    platform_source: PLATFORM_SOURCE,
    link_url: String(link_url),
    outbound: true,
  };
  if (link_domain) payload.link_domain = link_domain;
  if (destination_platform) payload.destination_platform = destination_platform;
  if (GA_DEBUG) payload.debug_mode = true;
  callGtag("event", "cross_platform_navigation", payload);
}

function hostnameMatchesPartner(hostname) {
  if (!hostname) return false;
  return CROSS_DOMAIN_HOSTS.some((h) => h.toLowerCase() === hostname.toLowerCase());
}

/**
 * Fire cross_platform_navigation when user follows a link to another partner host (not same host).
 */
export function registerOutboundLinkTracking() {
  if (typeof document === "undefined" || !MEASUREMENT_ID) return () => {};

  const onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const anchor = target.closest("a[href]");
    if (!anchor || anchor.getAttribute("href")?.startsWith("#")) return;

    let url;
    try {
      url = new URL(anchor.href, window.location.href);
    } catch {
      return;
    }

    if (url.protocol !== "http:" && url.protocol !== "https:") return;
    if (!hostnameMatchesPartner(url.hostname)) return;
    if (url.hostname === window.location.hostname) return;

    let destination_platform;
    if (url.hostname.includes("petitions.pro")) destination_platform = "petitions";
    else if (url.hostname.includes("zvarot.info")) destination_platform = "zvarot";
    else if (url.hostname.includes("pamaga.ch")) destination_platform = "pamaga";

    trackCrossPlatformNavigation({
      link_url: url.href,
      link_domain: url.hostname,
      destination_platform,
    });
  };

  document.addEventListener("click", onClick, true);
  return () => document.removeEventListener("click", onClick, true);
}

export { CROSS_DOMAIN_HOSTS, MEASUREMENT_ID };
